import { Matrix } from '@awayjs/core';
import { ImageSampler, AttributesBuffer, Float2Attributes, } from '@awayjs/stage';
import { MappingMode, Style } from '@awayjs/renderer';
import { TriangleElements } from '../elements/TriangleElements';
import { Shape } from '../renderables/Shape';
import { GraphicsFillStyle } from './GraphicsFillStyle';
import { GradientFillStyle } from './GradientFillStyle';
import { BitmapFillStyle } from './BitmapFillStyle';
import { GradientType } from './GradientType';
import { GraphicsFactoryHelper } from './GraphicsFactoryHelper';
import * as Tess2 from 'tess2-ts';
import createTess2Wasm from 'tess2-wasm';
import { MaterialManager } from '../managers/MaterialManager';
/**
 * The Graphics class contains a set of methods that you can use to create a
 * vector shape. Display objects that support drawing include Sprite and Shape
 * objects. Each of these classes includes a <code>graphics</code> property
 * that is a Graphics object. The following are among those helper functions
 * provided for ease of use: <code>drawRect()</code>,
 * <code>drawRoundRect()</code>, <code>drawCircle()</code>, and
 * <code>drawEllipse()</code>.
 *
 * <p>You cannot create a Graphics object directly from ActionScript code. If
 * you call <code>new Graphics()</code>, an exception is thrown.</p>
 *
 * <p>The Graphics class is final; it cannot be subclassed.</p>
 */
//@ts-ignore
var SHAPE_INFO = window.SHAPE_INFO = {
    total_time: 0,
    tess_time: 0,
    multy_contours: 0,
    single_contours: 0,
};
var FIXED_BASE = 1000;
var GraphicsFactoryFills = /** @class */ (function () {
    function GraphicsFactoryFills() {
    }
    GraphicsFactoryFills.prepareWasm = function () {
        var _this = this;
        if (!window.WebAssembly) {
            console.warn('[GraphicsFactoryFills] WASM not supported');
            this.wasmIsOk = false;
        }
        createTess2Wasm().then(function (_a) {
            var Tess = _a.Tess;
            GraphicsFactoryFills.Tess2Wasm = Tess;
        }).catch(function (e) {
            console.warn('[GraphicsFactoryFills] WASM initialisation fail:', e);
            _this.wasmIsOk = false;
        });
    };
    GraphicsFactoryFills.toFixed = function (val) {
        return (val * FIXED_BASE | 0) / FIXED_BASE;
    };
    GraphicsFactoryFills.nearest = function (x0, y0, x1, y1) {
        var dx = (x0 - x1);
        (dx < 0) && (dx = -dx);
        var dy = (y0 - y1);
        (dy < 0) && (dy = -dy);
        return (dx + dy) < this.EPS;
    };
    GraphicsFactoryFills.draw_pathes = function (targetGraphics) {
        //return;
        var pathes = targetGraphics.queued_fill_pathes;
        var len = pathes.length;
        for (var cp = 0; cp < len; cp++) {
            var path = pathes[cp];
            var pathStyle = path.style;
            // there are a bug with shapes
            var shape = targetGraphics.popEmptyFillShape();
            var elements = shape ? shape.elements : null;
            var target = elements ? elements.concatenatedBuffer : null;
            var newBuffer = this.pathToAttributesBuffer(path, false, target);
            if (!newBuffer || !newBuffer.length) {
                continue;
            }
            if (!elements) {
                elements = new TriangleElements(newBuffer);
                elements.setPositions(new Float2Attributes(newBuffer));
            }
            else {
                elements.invalidate();
                elements._numVertices = newBuffer.count;
            }
            elements.isDynamic = targetGraphics._clearCount > 0;
            //elements.setCustomAttributes("curves", new Float3Attributes(attributesBuffer));
            //elements.setUVs(new Float2Attributes(attributesBuffer));
            var material = void 0;
            var sampler = new ImageSampler();
            var style = new Style();
            switch (pathStyle.data_type) {
                case GraphicsFillStyle.data_type:
                    {
                        var obj = MaterialManager.get_material_for_color(pathStyle.color, pathStyle.alpha);
                        material = obj.material;
                        if (obj.colorPos) {
                            material.animateUVs = true;
                            style.addSamplerAt(sampler, material.getTextureAt(0));
                            style.uvMatrix = new Matrix(0, 0, 0, 0, obj.colorPos.x, obj.colorPos.y);
                        }
                        else {
                            style = sampler = null;
                        }
                        break;
                    }
                case GradientFillStyle.data_type:
                    {
                        var gradientStyle = (pathStyle);
                        var obj = MaterialManager.get_material_for_gradient(gradientStyle);
                        material = obj.material;
                        material.animateUVs = true;
                        style.addSamplerAt(sampler, material.getTextureAt(0));
                        style.uvMatrix = gradientStyle.getUVMatrix();
                        if (gradientStyle.type == GradientType.LINEAR) {
                            material.getTextureAt(0).mappingMode = MappingMode.LINEAR;
                        }
                        else if (gradientStyle.type == GradientType.RADIAL) {
                            sampler.imageRect = gradientStyle.uvRectangle;
                            material.imageRect = true;
                            material.getTextureAt(0).mappingMode = MappingMode.RADIAL;
                        }
                        break;
                    }
                case BitmapFillStyle.data_type:
                    {
                        var bitmapStyle = pathStyle;
                        //new ITexture(ImageUtils.getDefaultImage2D());//bitmapStyle.texture;
                        material = bitmapStyle.material;
                        //sampler.smooth = true;
                        sampler.repeat = bitmapStyle.repeat;
                        material.style.sampler = sampler;
                        material.animateUVs = true;
                        style.addSamplerAt(sampler, material.getTextureAt(0));
                        style.uvMatrix = bitmapStyle.getUVMatrix();
                        break;
                    }
            }
            shape = shape || Shape.getShape(elements);
            shape.style = style;
            shape.material = material;
            targetGraphics.addShapeInternal(shape);
        }
        //targetGraphics.queued_fill_pathes.length = 0;
    };
    GraphicsFactoryFills.prepareContours = function (graphicsPath, applyFix) {
        if (applyFix === void 0) { applyFix = false; }
        graphicsPath.prepare();
        var contours = graphicsPath._positions;
        var finalContours = [];
        for (var k = 0; k < contours.length; k++) {
            var contour = contours[k];
            // same as map, but without allocation
            var closed_1 = this.nearest(contour[0], contour[1], contour[contour.length - 2], contour[contour.length - 1]);
            // make sure start and end point of a contour are not the same
            if (closed_1) {
                contour.pop();
                contour.pop();
            }
            // all contours should already be prepared by GraphicsPath.prepare()
            // we only want to make sure that each contour contains at least 3 pairs of x/y positions
            // otherwise there is no way they can form a shape
            if (contour.length >= 6) {
                if (applyFix) {
                    // tess2 fix
                    // there are problems with small shapes
                    // encrease a size
                    var fixed = new Array(contour.length);
                    for (var i = 0, l = contour.length; i < l; i++) {
                        fixed[i] = this.toFixed(contour[i] * this.TESS_SCALE);
                    }
                    finalContours.push(fixed);
                }
                else {
                    finalContours.push(contour);
                }
            }
        }
        return finalContours;
    };
    GraphicsFactoryFills.runTesselator = function (graphicsPath) {
        var finalContours = this.prepareContours(graphicsPath, this.USE_TESS_FIX);
        if (finalContours.length > 0) {
            SHAPE_INFO.multy_contours += 1;
        }
        else {
            SHAPE_INFO.single_contours += 1;
        }
        var res = null;
        var tessWasm;
        if (finalContours.length > 0) {
            var useJSVersion = !this.Tess2Wasm || !this.wasmIsOk;
            var start = performance.now();
            if (!useJSVersion) {
                try {
                    this._lastTess = tessWasm = new this.Tess2Wasm();
                    tessWasm.addContours(finalContours);
                    res = tessWasm.tesselate();
                    SHAPE_INFO.tess_time += performance.now() - start;
                    return res;
                }
                catch (e) {
                    this.wasmIsOk = false;
                    useJSVersion = true;
                    console.warn('[GraphicsFactoryFills] Tess2Wasm crash, dowside to JS', e.message);
                }
            }
            if (useJSVersion) {
                if (!this.wasmIsOk) {
                    console.debug('[GraphicsFactoryFills] WASM Tess not loaded, JS will used.');
                }
                try {
                    res = Tess2.tesselate({
                        contours: finalContours,
                        windingRule: Tess2.WINDING_ODD,
                        elementType: Tess2.POLYGONS,
                        polySize: 3,
                        vertexSize: 2,
                        debug: true
                    });
                    SHAPE_INFO.tess_time += performance.now() - start;
                    return res;
                }
                catch (e) {
                    console.debug('[GraphicsFactoryFills] Error when trying to tesselate', finalContours);
                    return res = null;
                }
            }
        }
    };
    GraphicsFactoryFills.fillBuffer = function (result, finalVerts) {
        var numElems = result.elements.length;
        var scale = this.USE_TESS_FIX ? (1 / this.TESS_SCALE) : 1;
        var vindex = 0;
        var p1x = 0;
        var p1y = 0;
        var p2x = 0;
        var p2y = 0;
        var p3x = 0;
        var p3y = 0;
        for (var i = 0; i < numElems; i += 3) {
            p1x = scale * result.vertices[result.elements[i + 0] * 2 + 0];
            p1y = scale * result.vertices[result.elements[i + 0] * 2 + 1];
            p2x = scale * result.vertices[result.elements[i + 1] * 2 + 0];
            p2y = scale * result.vertices[result.elements[i + 1] * 2 + 1];
            p3x = scale * result.vertices[result.elements[i + 2] * 2 + 0];
            p3y = scale * result.vertices[result.elements[i + 2] * 2 + 1];
            if (GraphicsFactoryHelper.isClockWiseXY(p1x, p1y, p2x, p2y, p3x, p3y)) {
                finalVerts[vindex++] = p3x;
                finalVerts[vindex++] = p3y;
                finalVerts[vindex++] = p2x;
                finalVerts[vindex++] = p2y;
                finalVerts[vindex++] = p1x;
                finalVerts[vindex++] = p1y;
            }
            else {
                finalVerts[vindex++] = p1x;
                finalVerts[vindex++] = p1y;
                finalVerts[vindex++] = p2x;
                finalVerts[vindex++] = p2y;
                finalVerts[vindex++] = p3x;
                finalVerts[vindex++] = p3y;
            }
        }
        return finalVerts;
    };
    GraphicsFactoryFills.pathToAttributesBuffer = function (graphicsPath, closePath, target) {
        if (closePath === void 0) { closePath = true; }
        if (target === void 0) { target = null; }
        var start = performance.now();
        var resultVertexSize = graphicsPath.verts
            ? graphicsPath.verts.length
            : 0;
        var tesselatedVertexSize = 0;
        var res;
        var preparedBuffer = graphicsPath.pretesselatedBuffer;
        if (preparedBuffer) {
            resultVertexSize = preparedBuffer.length;
            console.debug('[GraphicsFactoryFills] Use prebuild buffer:', graphicsPath);
        }
        else {
            res = this.runTesselator(graphicsPath);
            if (res && res.elements.length > 0) {
                tesselatedVertexSize = res.elements.length * 2;
                resultVertexSize += res.elements.length * 2;
            }
        }
        var vertexSize = 2;
        if (!target) {
            target = new AttributesBuffer(Float32Array.BYTES_PER_ELEMENT * vertexSize, (resultVertexSize / vertexSize) | 0);
        }
        // resize is safe, it not rebuild buffer when count is same.
        // count - count of 2 dimension vertex, divide on 2
        target.count = (resultVertexSize / vertexSize) | 0;
        // fill direct to Float32Array
        var finalVerts = new Float32Array(target.buffer);
        if (preparedBuffer) {
            finalVerts.set(preparedBuffer);
        }
        else if (res) {
            this.fillBuffer(res, finalVerts);
            if (this._lastTess) {
                this._lastTess.dispose();
                this._lastTess = null;
            }
        }
        // merge poly vertex
        var vs = graphicsPath.verts.length;
        for (var i = 0; i < vs; i++) {
            finalVerts[tesselatedVertexSize + i] = graphicsPath.verts[i];
        }
        SHAPE_INFO.total_time += performance.now() - start;
        return target;
    };
    GraphicsFactoryFills.wasmIsOk = true;
    GraphicsFactoryFills.TESS_SCALE = 20;
    GraphicsFactoryFills.USE_TESS_FIX = true;
    GraphicsFactoryFills.EPS = 1.0 / FIXED_BASE;
    return GraphicsFactoryFills;
}());
export { GraphicsFactoryFills };
